import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CustomSelect from "components/CustomSelect";
import GoalButton from "./GoalButton";
import goalSelectStyles from "./goalSelectStyles";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [0, 1, 2, 3].map((offset) => {
  const year = currentYear + 1 - offset;
  return { value: year, label: String(year) };
});

// Filters for the goals CSV export: which template and which year.
// Year defaults to the current year each time the dialog opens.
function GoalExportDialog({ open, templates, onExport, onClose }) {
  const [templateId, setTemplateId] = useState("");
  const [year, setYear] = useState(currentYear);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateId("");
      setYear(currentYear);
    }
  }, [open]);

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.label,
  }));

  const handleExport = async () => {
    setIsExporting(true);
    const exported = await onExport({ templateId, year });
    setIsExporting(false);
    if (exported) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isExporting && onClose()}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle>Export goals</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <SuiTypography variant="button" color="text" display="block" mb={2}>
          Exports every engineer in this channel with their goal period for the chosen template and year. Engineers without
          goals are included as &quot;No goals set up&quot;.
        </SuiTypography>
        <SuiBox mb={2}>
          <SuiTypography variant="caption">Template</SuiTypography>
          <CustomSelect
            customStyles={goalSelectStyles}
            placeholder="Select a template…"
            options={templateOptions}
            value={templateOptions.find((o) => String(o.value) === String(templateId)) || null}
            onChange={(option) => setTemplateId(option.value)}
            menuPortalTarget={document.body}
          />
        </SuiBox>
        <SuiBox>
          <SuiTypography variant="caption">Year</SuiTypography>
          <CustomSelect
            customStyles={goalSelectStyles}
            options={YEAR_OPTIONS}
            value={YEAR_OPTIONS.find((o) => o.value === year)}
            onChange={(option) => setYear(option.value)}
            menuPortalTarget={document.body}
          />
        </SuiBox>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isExporting}>
          Cancel
        </GoalButton>
        <GoalButton
          variant="primary"
          icon="download"
          loading={isExporting}
          disabled={!templateId}
          onClick={handleExport}
        >
          Export
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

GoalExportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onExport: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GoalExportDialog;
